/**
 * Settings design primitives — recreate the Dashboard handoff's Settings card
 * pattern using the app design tokens (app orange #f54b01). Shared across all
 * settings panels so they share one cohesive look.
 *
 * Usage:
 *   <SettingsCard icon={Lock} title="Change Password" subtitle="…">
 *     …body…
 *   </SettingsCard>
 */
import React from 'react';
import { T } from '../../components/em/tokens';

// ── Card with an icon-tile header (the handoff's signature panel) ────────────
export const SettingsCard = ({
  icon: Icon,
  iconColor = T.orange,
  iconBg = T.orange50,
  title,
  subtitle,
  actions,
  children,
  bodyPadding = '24px',
  headerStyle = {},
  style = {},
}) => (
  <div
    style={{
      background: T.bgCard,
      border: `1px solid ${T.border}`,
      borderRadius: T.rXl,
      overflow: 'hidden',
      boxShadow: T.shadowSm,
      ...style,
    }}
  >
    {(title || Icon) && (
      <div
        style={{
          padding: '20px 24px',
          borderBottom: `1px solid ${T.divider}`,
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          ...headerStyle,
        }}
      >
        {Icon && (
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: 12,
              background: iconBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon size={20} color={iconColor} />
          </div>
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          {title && (
            <div style={{ font: `700 18px ${T.font}`, color: T.fg1, letterSpacing: '-0.02em' }}>
              {title}
            </div>
          )}
          {subtitle && (
            <div style={{ font: `400 13px ${T.font}`, color: T.fg5, marginTop: 2 }}>{subtitle}</div>
          )}
        </div>
        {actions && <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>{actions}</div>}
      </div>
    )}
    <div style={{ padding: bodyPadding }}>{children}</div>
  </div>
);

// ── Form atoms matching the handoff ─────────────────────────────────────────
export const SettingsLabel = ({ children, hint, style = {} }) => (
  <label style={{ display: 'block', font: `500 13px ${T.font}`, color: T.fg1, marginBottom: 8, ...style }}>
    {children}
    {hint && <span style={{ marginLeft: 6, color: T.fg7, fontWeight: 400 }}>{hint}</span>}
  </label>
);

export const SettingsInput = React.forwardRef(({ style = {}, invalid = false, ...p }, ref) => (
  <input
    ref={ref}
    {...p}
    style={{
      width: '100%',
      padding: '11px 14px',
      border: `1px solid ${invalid ? T.danger : T.border}`,
      borderRadius: 10,
      font: `400 14px ${T.font}`,
      color: T.fg1,
      outline: 'none',
      boxSizing: 'border-box',
      background: 'var(--em-bg-card)',
      transition: 'border-color 150ms',
      ...style,
    }}
    onFocus={(e) => {
      e.target.style.borderColor = invalid ? T.danger : T.orange;
      p.onFocus?.(e);
    }}
    onBlur={(e) => {
      e.target.style.borderColor = invalid ? T.danger : T.border;
      p.onBlur?.(e);
    }}
  />
));

export const SettingsSelect = ({ style = {}, children, ...p }) => (
  <select
    {...p}
    style={{
      width: '100%',
      padding: '11px 36px 11px 14px',
      border: `1px solid ${T.border}`,
      borderRadius: 10,
      font: `400 14px ${T.font}`,
      color: T.fg1,
      outline: 'none',
      boxSizing: 'border-box',
      background: 'var(--em-bg-card)',
      appearance: 'none',
      backgroundImage:
        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2399a1af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")",
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 12px center',
      ...style,
    }}
  >
    {children}
  </select>
);

// Field group = label + control + optional error/help line.
export const SettingsField = ({ label, hint, error, help, children, style = {} }) => (
  <div style={{ marginBottom: 18, ...style }}>
    {label && <SettingsLabel hint={hint}>{label}</SettingsLabel>}
    {children}
    {error && <div style={{ font: `400 11px ${T.font}`, color: T.danger, marginTop: 4 }}>{error}</div>}
    {help && !error && <div style={{ font: `400 11px ${T.font}`, color: T.fg7, marginTop: 4 }}>{help}</div>}
  </div>
);

// Brand gradient button used across settings panels.
export const SettingsButton = ({ children, icon: Icon, disabled, style = {}, ...p }) => (
  <button
    {...p}
    disabled={disabled}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      padding: '12px 20px',
      borderRadius: 10,
      border: 0,
      cursor: disabled ? 'default' : 'pointer',
      background: disabled ? T.bgMuted : T.gradient,
      color: disabled ? T.fg7 : '#fff',
      font: `700 14px ${T.font}`,
      letterSpacing: '0.01em',
      boxShadow: disabled ? 'none' : T.shadowBrand,
      transition: 'all 200ms ease',
      ...style,
    }}
  >
    {Icon && <Icon size={16} />}
    {children}
  </button>
);

// Gradient pill toggle (handoff style).
export const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!checked)}
    style={{
      width: 38,
      height: 22,
      borderRadius: 9999,
      border: 0,
      padding: 2,
      cursor: disabled ? 'default' : 'pointer',
      background: checked ? T.gradient : T.border,
      transition: 'background 180ms ease-out',
      display: 'inline-flex',
      alignItems: 'center',
      flexShrink: 0,
      opacity: disabled ? 0.6 : 1,
    }}
  >
    <span
      style={{
        display: 'block',
        width: 18,
        height: 18,
        borderRadius: 9999,
        background: 'var(--em-bg-card)',
        transform: checked ? 'translateX(16px)' : 'translateX(0)',
        transition: 'transform 180ms ease-out',
        boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
      }}
    />
  </button>
);

// Section title row inside a card body.
export const SettingsSectionTitle = ({ children, style = {} }) => (
  <div
    style={{
      font: `600 11px ${T.font}`,
      color: T.fg5,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      marginBottom: 10,
      ...style,
    }}
  >
    {children}
  </div>
);
